import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Description as DescriptionIcon,
  PendingActions as PendingIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { apiUrl } from '../utils/api';

interface QSDashboardProps {
  projectId: number | null;
  access?: 'none' | 'read' | 'write';
}

interface StatusSummary {
  draft: number;
  pending_approval: number;
  approved: number;
  rejected: number;
  total: number;
}

interface MRWithoutPO {
  mr_serial_no: string;
  mr_description: string | null;
  vendor_name: string | null;
  date_of_issue: string | null;
  status: string;
}

interface POTableRow {
  po_number: string;
  mr_serial_no: string | null;
  vendor_name: string | null;
  date_issued: string | null;
  status: string;
  currency: string | null;
}

interface ContractTableRow {
  contract_id: number;
  contract_number: string;
  title: string | null;
  contract_type: string;
  status: string;
  original_value: number | null;
  currency: string | null;
}

interface DashboardData {
  mrs_without_po: { count: number; list: MRWithoutPO[] };
  po_summary: StatusSummary;
  po_table: POTableRow[];
  contract_summary: StatusSummary;
  vo_summary: StatusSummary;
  contract_table: ContractTableRow[];
}

const TILE_COLORS = {
  draft: { bg: '#eef2ff', accent: '#6366f1', text: '#3730a3' },
  pending: { bg: '#fff7ed', accent: '#f59e0b', text: '#92400e' },
  approved: { bg: '#ecfdf5', accent: '#12b59f', text: '#065f46' },
  rejected: { bg: '#fef2f2', accent: '#ef4444', text: '#991b1b' },
  warning: { bg: '#fef3c7', accent: '#d97706', text: '#92400e' },
};

const StatTile: React.FC<{
  label: string;
  value: number;
  Icon: React.ElementType;
  palette: { bg: string; accent: string; text: string };
  onClick?: () => void;
  selected?: boolean;
  dimmed?: boolean;
}> = ({ label, value, Icon, palette, onClick, selected = false, dimmed = false }) => (
  <Card
    elevation={0}
    onClick={onClick}
    sx={{
      bgcolor: palette.bg,
      border: selected ? `2px solid ${palette.accent}` : `1px solid ${palette.accent}33`,
      borderRadius: 2,
      height: '100%',
      cursor: onClick ? 'pointer' : 'default',
      // Dimmed = some other tile in the SAME section is selected; gray this
      // one so the active filter stands out. Selected tiles get a stronger
      // border and a subtle ring to make the active state obvious.
      opacity: dimmed ? 0.45 : 1,
      filter: dimmed ? 'grayscale(0.5)' : 'none',
      transition: 'all 0.15s ease',
      boxShadow: selected ? `0 0 0 2px ${palette.accent}33` : 'none',
      '&:hover': onClick ? {
        boxShadow: selected
          ? `0 0 0 2px ${palette.accent}55`
          : `0 2px 8px ${palette.accent}33`,
        opacity: dimmed ? 0.7 : 1,
      } : {}
    }}
  >
    <CardContent sx={{ p: 2.25 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography
            variant="caption"
            sx={{ color: palette.text, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}
          >
            {label}
          </Typography>
          <Typography variant="h4" sx={{ color: palette.text, fontWeight: 700, mt: 0.5 }}>
            {value}
          </Typography>
        </Box>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: '50%',
            bgcolor: palette.accent,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon sx={{ color: '#fff', fontSize: 22 }} />
        </Box>
      </Box>
    </CardContent>
  </Card>
);

// Filter keys for the 4-tile status rows. Each of the three sections
// (PO / Contract / VO) maintains its own independent active-filter set,
// keyed on these same four values. A row passes when no filter is active
// or when at least one active filter matches its status.
type StatusFilterKey = 'draft' | 'pending_approval' | 'approved' | 'rejected';
const ALL_STATUS_KEYS: StatusFilterKey[] = ['draft', 'pending_approval', 'approved', 'rejected'];

const matchesStatusKey = (status: string, key: StatusFilterKey): boolean => {
  const s = (status || '').toLowerCase();
  switch (key) {
    case 'draft': return s === 'draft';
    case 'pending_approval': return s === 'pending approval';
    case 'approved': return s === 'approved';
    case 'rejected': return s === 'rejected';
    default: return false;
  }
};

const statusChipColor = (status: string): 'default' | 'primary' | 'success' | 'warning' | 'error' => {
  const s = (status || '').toLowerCase();
  if (s === 'approved') return 'success';
  if (s === 'rejected') return 'error';
  if (s === 'pending approval') return 'warning';
  return 'default';
};

// 4-tile status row reused for PO, Contract and VO summaries. When wired
// with `activeFilters` + `onToggle`, each tile becomes a clickable filter
// — selected tiles get a stronger border, the others in this row dim so
// the active filter stands out. Sections are independent: a PO filter does
// not dim Contract or VO tiles.
const StatusTileRow: React.FC<{
  summary: StatusSummary;
  activeFilters?: Set<StatusFilterKey>;
  onToggle?: (key: StatusFilterKey) => void;
}> = ({ summary, activeFilters, onToggle }) => {
  const sectionHasFilter = !!activeFilters && activeFilters.size > 0;
  const tileProps = (key: StatusFilterKey) => ({
    onClick: onToggle ? () => onToggle(key) : undefined,
    selected: activeFilters?.has(key) ?? false,
    dimmed: sectionHasFilter && !(activeFilters?.has(key) ?? false),
  });
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
        gap: 2,
        mb: 2,
      }}
    >
      <StatTile label="Draft" value={summary.draft} Icon={DescriptionIcon} palette={TILE_COLORS.draft} {...tileProps('draft')} />
      <StatTile label="Pending Approval" value={summary.pending_approval} Icon={PendingIcon} palette={TILE_COLORS.pending} {...tileProps('pending_approval')} />
      <StatTile label="Approved" value={summary.approved} Icon={CheckCircleIcon} palette={TILE_COLORS.approved} {...tileProps('approved')} />
      <StatTile label="Rejected" value={summary.rejected} Icon={CancelIcon} palette={TILE_COLORS.rejected} {...tileProps('rejected')} />
    </Box>
  );
};

const formatMoney = (amount: number | null, currency: string | null): string => {
  if (amount === null || amount === undefined) return '—';
  const symbol = currency || '';
  return `${symbol} ${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`.trim();
};

const QSDashboard: React.FC<QSDashboardProps> = ({ projectId, access = 'none' }) => {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Three independent tile-based filter sets — PO tiles filter the PO table,
  // Contract tiles filter contracts in the combined Contracts+VOs table, and
  // VO tiles filter VOs in that same combined table. Independent so a PO
  // filter does not dim Contract/VO tiles, matching the Site Dashboard UX.
  const [poFilters, setPoFilters] = useState<Set<StatusFilterKey>>(new Set());
  const [contractFilters, setContractFilters] = useState<Set<StatusFilterKey>>(new Set());
  const [voFilters, setVoFilters] = useState<Set<StatusFilterKey>>(new Set());

  // Curried toggler for any of the three filter sets — flips a key in the
  // set so clicking the same tile twice removes it (toggle behaviour).
  const makeToggle = (
    setter: React.Dispatch<React.SetStateAction<Set<StatusFilterKey>>>
  ) => (key: StatusFilterKey) => {
    setter(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };
  const togglePo = makeToggle(setPoFilters);
  const toggleContract = makeToggle(setContractFilters);
  const toggleVo = makeToggle(setVoFilters);

  // Esc clears every active tile filter across all three sections at once —
  // single shortcut, easy to recover from any filter state.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (poFilters.size === 0 && contractFilters.size === 0 && voFilters.size === 0) return;
      setPoFilters(new Set());
      setContractFilters(new Set());
      setVoFilters(new Set());
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [poFilters.size, contractFilters.size, voFilters.size]);

  useEffect(() => {
    if (access === 'none') return;
    if (!projectId) {
      setData(null);
      return;
    }
    setLoading(true);
    setError(null);
    fetch(apiUrl(`/api/qs-dashboard?project_id=${projectId}`), {
      credentials: 'include',
    })
      .then(async (res) => {
        const json = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(json.error || `Failed to load (${res.status})`);
        }
        return json;
      })
      .then((json: DashboardData) => setData(json))
      .catch((e: any) => setError(e.message || 'Failed to load dashboard'))
      .finally(() => setLoading(false));
  }, [projectId, access]);

  // Latest 10 POs by date_issued desc (nulls last). When PO tile filters are
  // active, only POs whose status matches at least one active filter are kept.
  const recentPOs = useMemo(() => {
    if (!data) return [];
    const active = ALL_STATUS_KEYS.filter(k => poFilters.has(k));
    const filtered = active.length === 0
      ? data.po_table
      : data.po_table.filter(po => active.some(k => matchesStatusKey(po.status, k)));
    return [...filtered]
      .sort((a, b) => (b.date_issued || '').localeCompare(a.date_issued || ''))
      .slice(0, 10);
  }, [data, poFilters]);

  // Latest 10 contracts/VOs by contract_id desc (stable recency proxy).
  // Filtering applies independently to Contract rows (via contractFilters)
  // and VO rows (via voFilters) — a row passes if its type has no active
  // filter OR its status matches one of its type's active filters. Result
  // is the union so e.g. "Contract: Draft" + "VO: Approved" works.
  const recentContracts = useMemo(() => {
    if (!data) return [];
    const activeC = ALL_STATUS_KEYS.filter(k => contractFilters.has(k));
    const activeV = ALL_STATUS_KEYS.filter(k => voFilters.has(k));
    const filtered = data.contract_table.filter(c => {
      const isVO = c.contract_type === 'variation_order';
      if (isVO) {
        return activeV.length === 0 || activeV.some(k => matchesStatusKey(c.status, k));
      }
      return activeC.length === 0 || activeC.some(k => matchesStatusKey(c.status, k));
    });
    return [...filtered]
      .sort((a, b) => b.contract_id - a.contract_id)
      .slice(0, 10);
  }, [data, contractFilters, voFilters]);

  if (access === 'none') {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">You do not have access to the QS Dashboard.</Alert>
      </Box>
    );
  }

  if (!projectId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">Select a project from the sidebar to view the QS dashboard.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {data && !loading && (
        <>
          {/* ----- MRs Without PO (QS action list) ----- */}
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5, color: '#374151' }}>
            Approved MRs Without PO
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr' },
              gap: 2,
              mb: 2,
            }}
          >
            <StatTile
              label="Pending PO Action"
              value={data.mrs_without_po.count}
              Icon={WarningIcon}
              palette={TILE_COLORS.warning}
            />
          </Box>
          <Card elevation={0} sx={{ mb: 4, border: '1px solid #e5e7eb', borderRadius: 2 }}>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ p: 2, borderBottom: '1px solid #eef0f3' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#374151' }}>
                  Approved Material Requisitions awaiting PO
                </Typography>
                <Typography variant="caption" sx={{ color: '#6b7280' }}>
                  Every row needs a Purchase Order to be raised.
                </Typography>
              </Box>
              <TableContainer component={Paper} elevation={0}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f9fafb' }}>
                      <TableCell sx={{ fontWeight: 700 }}>MR #</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Vendor</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.mrs_without_po.list.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ color: '#9ca3af', py: 3 }}>
                          All approved MRs have a PO raised. Nothing pending.
                        </TableCell>
                      </TableRow>
                    )}
                    {data.mrs_without_po.list.map((mr) => (
                      <TableRow key={mr.mr_serial_no} hover>
                        <TableCell sx={{ fontWeight: 600 }}>{mr.mr_serial_no}</TableCell>
                        <TableCell sx={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {mr.mr_description || '—'}
                        </TableCell>
                        <TableCell>{mr.vendor_name || '—'}</TableCell>
                        <TableCell>{mr.date_of_issue || '—'}</TableCell>
                        <TableCell>
                          <Chip label={mr.status} size="small" color={statusChipColor(mr.status)} onClick={(e) => e.stopPropagation()} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* ----- PO Status ----- */}
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5, color: '#374151' }}>
            Purchase Orders
          </Typography>
          <StatusTileRow summary={data.po_summary} activeFilters={poFilters} onToggle={togglePo} />
          <Card elevation={0} sx={{ mb: 4, border: '1px solid #e5e7eb', borderRadius: 2 }}>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ p: 2, borderBottom: '1px solid #eef0f3' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#374151' }}>
                  Purchase Orders
                </Typography>
                <Typography variant="caption" sx={{ color: '#6b7280' }}>
                  Showing {recentPOs.length} of {data.po_summary.total} total
                  {poFilters.size > 0 && (
                    <Box component="span" sx={{ ml: 1, color: '#1976d2', fontWeight: 600 }}>
                      · Filtered ({poFilters.size}) — press Esc to clear
                    </Box>
                  )}
                </Typography>
              </Box>
              <TableContainer component={Paper} elevation={0}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f9fafb' }}>
                      <TableCell sx={{ fontWeight: 700 }}>PO #</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>MR #</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Vendor</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Date Issued</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentPOs.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ color: '#9ca3af', py: 3 }}>
                          No Purchase Orders for this project yet.
                        </TableCell>
                      </TableRow>
                    )}
                    {recentPOs.map((po) => (
                      <TableRow key={po.po_number} hover>
                        <TableCell sx={{ fontWeight: 600 }}>{po.po_number}</TableCell>
                        <TableCell>{po.mr_serial_no || '—'}</TableCell>
                        <TableCell>{po.vendor_name || '—'}</TableCell>
                        <TableCell>{po.date_issued || '—'}</TableCell>
                        <TableCell>
                          <Chip label={po.status} size="small" color={statusChipColor(po.status)} onClick={(e) => e.stopPropagation()} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* ----- Contract Status ----- */}
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5, color: '#374151' }}>
            Contracts
            <Box component="span" sx={{ ml: 1, fontSize: '0.75rem', fontWeight: 500, color: '#6b7280' }}>
              ({data.contract_summary.total} total)
            </Box>
          </Typography>
          <StatusTileRow summary={data.contract_summary} activeFilters={contractFilters} onToggle={toggleContract} />

          {/* ----- VO Status ----- */}
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5, color: '#374151' }}>
            Variation Orders (VOs)
            <Box component="span" sx={{ ml: 1, fontSize: '0.75rem', fontWeight: 500, color: '#6b7280' }}>
              ({data.vo_summary.total} total)
            </Box>
          </Typography>
          <StatusTileRow summary={data.vo_summary} activeFilters={voFilters} onToggle={toggleVo} />

          {/* ----- Recent Contracts + VOs table ----- */}
          <Card elevation={0} sx={{ border: '1px solid #e5e7eb', borderRadius: 2 }}>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ p: 2, borderBottom: '1px solid #eef0f3' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#374151' }}>
                  Contracts &amp; VOs
                </Typography>
                <Typography variant="caption" sx={{ color: '#6b7280' }}>
                  Showing {recentContracts.length} of {data.contract_table.length} total
                  {(contractFilters.size > 0 || voFilters.size > 0) && (
                    <Box component="span" sx={{ ml: 1, color: '#1976d2', fontWeight: 600 }}>
                      · Filtered ({contractFilters.size + voFilters.size}) — press Esc to clear
                    </Box>
                  )}
                </Typography>
              </Box>
              <TableContainer component={Paper} elevation={0}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f9fafb' }}>
                      <TableCell sx={{ fontWeight: 700 }}>Number</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Title</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">Original Value</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentContracts.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ color: '#9ca3af', py: 3 }}>
                          No Contracts or VOs for this project yet.
                        </TableCell>
                      </TableRow>
                    )}
                    {recentContracts.map((c) => (
                      <TableRow key={c.contract_id} hover>
                        <TableCell sx={{ fontWeight: 600 }}>{c.contract_number}</TableCell>
                        <TableCell sx={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {c.title || '—'}
                        </TableCell>
                        <TableCell>
                          {/* Type chip — outlined variant with no icon, mirroring the
                              PM Dashboard pattern. The earlier icon-prop version triggered
                              an "onClick is not a function" error inside MUI Chip when
                              the user clicked the icon, so the icon prop is dropped here. */}
                          {c.contract_type === 'variation_order' ? (
                            <Chip label="VO" size="small" color="secondary" variant="outlined" onClick={(e) => e.stopPropagation()} />
                          ) : (
                            <Chip label="Contract" size="small" color="primary" variant="outlined" onClick={(e) => e.stopPropagation()} />
                          )}
                        </TableCell>
                        <TableCell align="right">{formatMoney(c.original_value, c.currency)}</TableCell>
                        <TableCell>
                          <Chip label={c.status} size="small" color={statusChipColor(c.status)} onClick={(e) => e.stopPropagation()} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
};

export default QSDashboard;
